import { createWithEqualityFn } from 'zustand/traditional'
import { persist, createJSONStorage } from 'zustand/middleware'
import { Cookies } from 'react-cookie'
import axios from 'axios'

interface AuthStore {
	accessToken?: string
	refreshToken?: string
	getCookieAccessToken: () => string | undefined
	getCookieRefreshToken: () => string | undefined
	setAccessToken: (accessToken: string) => void
	setRefreshToken: (refreshToken: string) => void
	setCookieAccessToken: (accessToken: string) => void
	setCookieRefreshToken: (refreshToken: string) => void
	removeCookieRefreshToken: () => void
	removeCookieAccessToken: () => void
	removeAccessToken: () => void
	removeRefreshToken: () => void
	verifyToken: () => Promise<{ success: boolean; data: any }>
	validateEmailAvailable: (email: string) => Promise<{ success: boolean; data: any }>
	clearState: () => void
}

export const useAuthStore = createWithEqualityFn(
	persist<AuthStore>(
		(set, get) => ({
			accessToken: undefined,
			refreshToken: undefined,
			getCookieAccessToken: () => {
				const cookie = new Cookies()
				return cookie.get('kumopack-supplier-access-token')
			},
			getCookieRefreshToken: () => {
				const cookie = new Cookies()
				return cookie.get('kumopack-supplier-refresh-token')
			},
			setCookieAccessToken: (accessToken: string) => {
				const cookie = new Cookies()
				cookie.set('kumopack-supplier-access-token', accessToken, {
					path: '/',
					domain: process.env.NODE_ENV === 'production' ? '.kumopack.com' : undefined,
				})
			},
			setCookieRefreshToken: (refreshToken: string) => {
				const cookie = new Cookies()
				cookie.set('kumopack-supplier-refresh-token', refreshToken, {
					path: '/',
					domain: process.env.NODE_ENV === 'production' ? '.kumopack.com' : undefined,
				})
			},
			removeCookieAccessToken: () => {
				const cookie = new Cookies()
				cookie.remove('kumopack-supplier-access-token', {
					path: '/',
					domain: process.env.NODE_ENV === 'production' ? '.kumopack.com' : undefined,
				})
			},
			removeCookieRefreshToken: () => {
				const cookie = new Cookies()
				cookie.remove('kumopack-supplier-refresh-token', {
					path: '/',
					domain: process.env.NODE_ENV === 'production' ? '.kumopack.com' : undefined,
				})
			},
			setAccessToken: (accessToken: string) => set({ accessToken }),
			removeAccessToken: () => set({ accessToken: undefined }),
			setRefreshToken: (refreshToken: string) => set({ refreshToken }),
			removeRefreshToken: () => set({ refreshToken: undefined }),
			verifyToken: async () => {
				try {
					const res = await axios.post('/auth-supplier/verify-token', undefined, {
						headers: {
							Authorization: `Bearer ${get().accessToken}`,
						},
					})

					return { success: true, data: res.data }
				} catch (err: any) {
					return {
						success: false,
						data: err?.response?.data?.message?.toString(),
					}
				}
			},
			validateEmailAvailable: async (email: string) => {
				return axios
					.get(`/auth-supplier/email-available?email=${email}`)
					.then(res => {
						return { success: true, data: res.data }
					})
					.catch(err => {
						return {
							success: false,
							data: err?.response?.data?.message?.toString(),
						}
					})
			},
			clearState: () =>
				set({
					accessToken: undefined,
					refreshToken: undefined,
				}),
		}),
		{
			name: 'kumopack-supplier-auth',
			storage: createJSONStorage(() => localStorage),
		}
	),
	Object.is
)
